import { setUnsavedDirty } from '@/utils/unsavedChanges';
import { useEffect } from 'react';

/**
 * Publish a settings form's dirty state to the global navigation guard
 * (see utils/unsavedChanges.ts). Clears the flag on unmount so leaving the form -
 * or landing on a form that doesn't track dirty state - never reports stale.
 *
 * Usually you don't call this directly: <SettingsSaveNav> calls it for you.
 */
export function useUnsavedGuard(isDirty: boolean): void {
  useEffect(() => {
    setUnsavedDirty(isDirty);
  }, [isDirty]);

  useEffect(() => () => setUnsavedDirty(false), []);

  // Framework7's `beforeLeave` only intercepts in-app navigation (Back, nav
  // rows). A browser refresh, tab close, or hardware/browser Back bypasses the
  // router entirely - only the native `beforeunload` event fires there. Register
  // it while dirty so the browser shows its standard "leave site?" prompt and the
  // half-entered form isn't silently lost.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers require a truthy returnValue to trigger the prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);
}
